import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { adminApi, SALE_STATUS_LABEL, type SaleListItem, type SalesPage } from "@/lib/admin";
import { formatPln } from "@/lib/utils";

const STATUS_FILTERS = ["", "Created", "Pending", "Paid", "Answered", "Invoiced", "Failed", "Cancelled"];
const PAGE_SIZE = 50;

export function AdminSales() {
  const [page, setPage] = useState<SalesPage | null>(null);
  const [skip, setSkip] = useState(0);
  const [status, setStatus] = useState<string>("");
  const [email, setEmail] = useState("");
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    adminApi
      .sales({ status: status || undefined, email: email || undefined, skip, take: PAGE_SIZE })
      .then((d) => {
        if (active) setPage(d);
      })
      .catch((e) => {
        if (active) setErr(e.message);
      });
    return () => {
      active = false;
    };
  }, [status, email, skip]);

  return (
    <div className="space-y-4">
      <header className="flex items-baseline justify-between gap-4 flex-wrap">
        <h1 className="font-display font-bold text-3xl">Sprzedaże</h1>
        {page && (
          <p className="text-sm font-mono text-carp-ink/60">
            {page.items.length} z {page.total}
          </p>
        )}
      </header>

      <div className="brut bg-carp-paper p-4 grid sm:grid-cols-3 gap-3">
        <select
          className="input-brut"
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setSkip(0);
          }}
        >
          {STATUS_FILTERS.map((s) => (
            <option key={s} value={s}>
              {s || "wszystkie statusy"}
            </option>
          ))}
        </select>
        <input
          className="input-brut sm:col-span-2"
          placeholder="filtr po e-mailu (substring)"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            setSkip(0);
          }}
        />
      </div>

      {err && (
        <div className="brut bg-carp-destructive text-white p-3 text-sm">{err}</div>
      )}

      <div className="brut bg-carp-paper overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-carp-ink text-carp-paper text-left font-display uppercase text-xs">
            <tr>
              <th className="p-2">Kiedy</th>
              <th className="p-2">Status</th>
              <th className="p-2 text-right">Kwota</th>
              <th className="p-2">E-mail</th>
              <th className="p-2">Wiadomość</th>
              <th className="p-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-carp-ink/10">
            {page?.items.map((s) => (
              <Row key={s.id} sale={s} />
            ))}
            {page && page.items.length === 0 && (
              <tr>
                <td colSpan={6} className="p-6 text-center text-carp-ink/50">
                  Brak wyników
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center gap-2 justify-end text-sm">
        <button
          type="button"
          className="btn-ghost"
          onClick={() => setSkip(Math.max(0, skip - PAGE_SIZE))}
          disabled={skip === 0}
        >
          ← poprzednie
        </button>
        <button
          type="button"
          className="btn-ghost"
          onClick={() => setSkip(skip + PAGE_SIZE)}
          disabled={!page || page.items.length < PAGE_SIZE}
        >
          następne →
        </button>
      </div>
    </div>
  );
}

function Row({ sale }: { sale: SaleListItem }) {
  return (
    <tr className="hover:bg-carp-yellow/30">
      <td className="p-2 font-mono text-xs text-carp-ink/70 whitespace-nowrap">
        {new Date(sale.createdAt).toLocaleString("pl-PL")}
      </td>
      <td className="p-2">
        <StatusPill status={sale.status} />
      </td>
      <td className="p-2 text-right font-display font-bold">
        {formatPln(sale.amount)}
      </td>
      <td className="p-2 text-xs">{sale.email}</td>
      <td className="p-2 text-xs italic text-carp-ink/80 max-w-[260px] truncate">
        „{sale.messagePreview}"
      </td>
      <td className="p-2 text-right">
        <Link to={`/admin/sales/${sale.id}`} className="link-brut text-xs">
          szczegóły →
        </Link>
      </td>
    </tr>
  );
}

function StatusPill({ status }: { status: number }) {
  const label = SALE_STATUS_LABEL[status] ?? String(status);
  const bg =
    status === 3 || status === 4
      ? "bg-carp-lime"
      : status === 2
        ? "bg-carp-cyan"
        : status === 9 || status === 10
          ? "bg-carp-destructive text-white"
          : "bg-carp-yellow";
  return (
    <span className={`chip ${bg}`}>{label}</span>
  );
}
